import React, { useEffect, useState } from 'react';
import { HONOR_CODE_LINES, HONOR_CODE_SUBLINE } from '../../constants/honorCode';

function HonorCodeBlockquote() {
	return (
		<blockquote className="mt-2 border-l-4 border-blue-600 pl-4 text-sm leading-relaxed text-gray-800 dark:text-dark-text">
			{HONOR_CODE_LINES.map((line) => (
				<p key={line}>{line}</p>
			))}
		</blockquote>
	);
}

export default function HonorCodeModal({
	isOpen,
	onClose,
	onConfirm,
	title = 'Accept challenge',
	acceptingAsName,
	challengeTitle,
	confirmLabel = 'Accept challenge',
	loading = false,
	error = ''
}) {
	const [agreed, setAgreed] = useState(false);
	const wireAccept = Boolean(acceptingAsName && challengeTitle);
	const canConfirm = agreed && !loading;

	useEffect(() => {
		if (isOpen) setAgreed(false);
	}, [isOpen]);

	if (!isOpen) return null;

	function handleClose() {
		if (loading) return;
		setAgreed(false);
		onClose();
	}

	function handleConfirm() {
		if (!agreed || loading) return;
		onConfirm();
	}

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/75 px-4"
			onClick={handleClose}
			role="presentation"
		>
			<div
				className="w-full max-w-md rounded-2xl border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card p-5 shadow-xl text-gray-900 dark:text-dark-text"
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-labelledby={wireAccept ? 'honor-wire-heading' : 'honor-title-heading'}
			>
				{wireAccept ? (
					<>
						<p id="honor-wire-heading" className="text-lg font-semibold">
							{acceptingAsName},
						</p>
						<p className="mt-3 text-base leading-relaxed text-gray-800 dark:text-dark-text">
							You are accepting the Challenge:{' '}
							<span className="font-semibold text-gray-900 dark:text-dark-text">{challengeTitle}</span>
						</p>
						<p className="mt-5 text-sm font-medium text-gray-700 dark:text-dark-text-secondary">
							Repeat after me:
						</p>
						<p className="mt-3 text-xs uppercase tracking-wide text-gray-500 dark:text-dark-text-secondary">
							{HONOR_CODE_SUBLINE}
						</p>
						<HonorCodeBlockquote />
					</>
				) : (
					<>
						<h3 id="honor-title-heading" className="text-lg font-semibold">
							{title}
						</h3>
						<p className="mt-1 text-xs uppercase tracking-wide text-gray-500 dark:text-dark-text-secondary">
							{HONOR_CODE_SUBLINE}
						</p>
						<HonorCodeBlockquote />
					</>
				)}
				<label className="mt-4 flex items-start gap-3 cursor-pointer">
					<input
						type="checkbox"
						checked={agreed}
						onChange={(e) => setAgreed(e.target.checked)}
						disabled={loading}
						className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
					/>
					<span className="text-sm text-gray-700 dark:text-dark-text-secondary">I agree</span>
				</label>
				{error && (
					<p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
						{error}
					</p>
				)}
				<div className="mt-5 flex justify-end gap-3 border-t border-gray-200 dark:border-dark-border pt-4">
					<button
						type="button"
						onClick={handleClose}
						disabled={loading}
						className="rounded-xl border border-gray-300 dark:border-dark-border px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-bg"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleConfirm}
						disabled={!canConfirm}
						aria-disabled={!canConfirm}
						className={
							canConfirm
								? 'rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700'
								: 'rounded-xl bg-gray-300 dark:bg-gray-600 px-4 py-2.5 text-sm font-semibold text-gray-500 dark:text-gray-400 cursor-not-allowed'
						}
					>
						{loading ? 'Working…' : confirmLabel}
					</button>
				</div>
			</div>
		</div>
	);
}
